import { useField, useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';

import { ProductMessage } from '../../../../types/ProductMessage';
import { Text } from '../../../cdl/Text/Text';
import { FormikHumanReadableEnumValue } from '../../form-elements/formik/FormikHumanReadableEnumValue';
import { formatNumber } from '../../helpers/formatNumber.helper';
import { formatTotalPrice } from '../../helpers/formatTotal.helper';
import { Characters } from '../../helpers/unicode.helper';
import { Item } from '../../models/item.model';
import { ItemModel } from '../../models/ItemModel';
import { Td } from '../../Table/Td';
import { Tr } from '../../Table/Tr';
import { Box } from '../../ui/Box';
import { PackTypeDisplay } from '../components/PackTypeDisplay';
import { PriceDisplay } from '../components/PriceDisplay';
import { ProductNameDisplay } from '../components/ProductNameDisplay';
import { ProductRowAction } from '../components/ProductRowAction';
import { ProductTd } from '../components/ProductTd';
import { UnitDisplay } from '../components/UnitDisplay';
import { UnitsDisplay } from '../components/UnitsDisplay';
import { UnitSizeDisplay } from '../components/UnitSizeDisplay';
import { useProductTableConfig } from '../hooks/useProductTableConfig';

interface ProductRowProps {
    name: string;
    index: number;
    isDeletable: boolean;
    isLastRemainingEntry: boolean;
    isLastSelectedEntry: boolean;
    onRowRemoveClicked: () => void;
}

export const ProductRow = (props: ProductRowProps) => {
    const { editable, showPrices, editPrices } = useProductTableConfig();
    const { setFieldTouched, validateForm } = useFormikContext();
    const previousValidationTriggerRef = useRef<{ productId?: string; packType?: string; deleted?: boolean } | null>(
        null
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [itemField, _, itemFieldHelpers] = useField(props.name);

    const item = itemField.value;

    useEffect(() => {
        if (!item.compute) {
            return;
        }

        const computedItem = new Item(item);
        computedItem.compute();

        const hasVolumeChanged = item.volume !== computedItem.volume;
        const hasTotalChanged =
            item.total?.value !== computedItem.total?.value || item.total?.currency !== computedItem.total?.currency;

        if (!hasVolumeChanged && !hasTotalChanged) {
            return;
        }

        itemFieldHelpers.setValue(computedItem, false);
    }, [item, itemFieldHelpers]);

    useEffect(() => {
        if (!editable) {
            return;
        }

        const currentTriggerState = {
            productId: item.productId,
            packType: item.packType,
            deleted: item.deleted,
        };

        if (previousValidationTriggerRef.current === null) {
            previousValidationTriggerRef.current = currentTriggerState;
            return;
        }

        if (
            previousValidationTriggerRef.current.productId === currentTriggerState.productId &&
            previousValidationTriggerRef.current.packType === currentTriggerState.packType &&
            previousValidationTriggerRef.current.deleted === currentTriggerState.deleted
        ) {
            return;
        }

        previousValidationTriggerRef.current = currentTriggerState;
        setFieldTouched('items', true, false);
        validateForm();
    }, [editable, item.deleted, item.packType, item.productId, setFieldTouched, validateForm]);

    const onProductSelect = (product: ProductMessage) => {
        const existingItem: ItemModel = itemField.value;

        const previousPrice = {
            value: existingItem.price?.value ?? null,
            currency: existingItem.price?.currency ?? null,
        };

        const newItem = new Item({
            ...existingItem,
            productId: product.id,
            product,
            price: editPrices && previousPrice.value ? previousPrice : undefined,
        });

        newItem.initWithProduct(product);

        itemFieldHelpers.setValue(newItem, false);
    };

    return (
        <Tr inactive={item.deleted} focused={item.added && !item.deleted}>
            <Td>
                <ProductNameDisplay
                    name={props.name}
                    vesselIdName="vesselId"
                    onSelect={onProductSelect}
                    editable={editable}
                />
            </Td>
            <ProductTd>
                <Box display="flex" alignItems="center" height="40px">
                    <Text variant="small">
                        <FormikHumanReadableEnumValue
                            name={`${props.name}.product.group`}
                            enumType="productGroup"
                            fallback={Characters.EN_DASH}
                        />
                    </Text>
                </Box>
            </ProductTd>
            <ProductTd>
                <PackTypeDisplay name={props.name} editable={editable} />
            </ProductTd>
            <ProductTd>
                <UnitSizeDisplay name={props.name} editable={editable} />
            </ProductTd>
            <ProductTd>
                <UnitsDisplay name={props.name} editable={editable} itemDeleted={item.deleted} index={props.index} />
            </ProductTd>
            <ProductTd>
                <Text variant="small" textAlign="right">
                    {item.volume ? formatNumber({ number: item.volume, maximumFractionDigits: 3 }) : Characters.EN_DASH}
                </Text>
            </ProductTd>
            <ProductTd>
                <UnitDisplay name={props.name} editable={editable} />
            </ProductTd>

            {showPrices ? (
                <>
                    <ProductTd>
                        <PriceDisplay name={props.name} editable={editPrices} />
                    </ProductTd>

                    <ProductTd>
                        <Text variant="small" textAlign="right">
                            {item.total?.value
                                ? formatTotalPrice({
                                      value: item.total.value,
                                      currency: item.total?.currency || 'USD',
                                  })
                                : Characters.EN_DASH}
                        </Text>
                    </ProductTd>
                </>
            ) : null}

            {editable ? (
                <ProductTd>
                    <Box display="flex" width="32px" justifyContent="center">
                        <ProductRowAction
                            name={props.name}
                            item={item}
                            isDeletable={props.isDeletable}
                            isLastRemainingEntry={props.isLastRemainingEntry}
                            isLastSelectedEntry={props.isLastSelectedEntry}
                            onRowRemoveClicked={props.onRowRemoveClicked}
                        />
                    </Box>
                </ProductTd>
            ) : null}
        </Tr>
    );
};
